/**
 * ToolsBox — style objects for the Synth VR Tools box (SRVR-0351). Theme tokens and accent/tint-derived
 * OKLCH only, matching the Camera View buttons above it, so the box re-tints with the accent theme.
 */
import type { CSSProperties } from 'react';
import { border, color, font, surface, ui } from '../ui/theme';
import { cameraBtnStyle } from '../ui/editorSharedStyles';

// minmax(0, 1fr), not 1fr: a bare 1fr column will not shrink below its label's longest word, so
// "Smoothing" and "Rotation" widened their columns and left Scaling's narrower.
export const toolGridStyle: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 4 };

/** The Tooltip wrapper round each tool button: it fills its grid cell so the buttons can fill it in turn. */
export const toolCellStyle: CSSProperties = { display: 'flex', minWidth: 0 };

/** A tool button: the Camera View button, lit while its tool is open. Fills its cell, so all three are one size. */
export function toolBtnStyle(active: boolean): CSSProperties {
  return {
    ...cameraBtnStyle,
    flex: 1,
    minWidth: 0,
    background: active
      ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
      : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
    borderColor: active ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
    color: active ? color.accent : color.inkFaint,
    boxShadow: 'none',
  };
}

export const toolLabelStyle: CSSProperties = { fontSize: 9, maxWidth: '100%', textAlign: 'center', lineHeight: 1.15 };

export const panelStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6, paddingTop: 6,
  borderTop: `1px solid ${border.subtle}`, fontFamily: font.sans,
};

export const hintStyle: CSSProperties = { fontSize: 9, lineHeight: 1.45, color: color.inkFaint };

export const statusStyle: CSSProperties = { ...hintStyle, color: color.accentSoft, fontWeight: 600, textAlign: 'center' };

export const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 4 };

/** A small pill button (steppers, the rotation steps, Reset/Apply). */
export const pillBtnStyle: CSSProperties = {
  flex: 1, padding: '5px 4px', borderRadius: 999, cursor: 'pointer',
  fontFamily: font.sans, fontSize: 9, fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap',
  color: color.accentSoft,
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
};

export const pillBtnDisabledStyle: CSSProperties = { ...pillBtnStyle, color: color.inkGhost, cursor: 'default', opacity: 0.6 };

/** The committing button of a row (Apply, OK). */
export const primaryPillStyle: CSSProperties = {
  ...pillBtnStyle,
  color: color.accent,
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.18)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
};

export const selectStyle: CSSProperties = { ...ui.input, fontSize: 10, padding: '4px 6px', width: '100%', background: surface.input };

export const checkRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none',
  fontSize: 10, color: color.inkDim,
};

export const checkboxStyle: CSSProperties = { accentColor: color.accent, margin: 0, cursor: 'pointer' };

export const subLabelStyle: CSSProperties = { ...ui.subLabel, marginTop: 2 };
